/**
 * SettingsPage curates the two operator-facing cards from the reference
 * design (Agency identity, Operations thresholds) on top of the real
 * backend surfaces:
 *  - Agency name/timezone/language: GET/PUT /agencies/{id} (the agencies
 *    table already has these columns — no schema change needed).
 *  - Thresholds: the same schema-driven /settings + /settings/schema the
 *    old page rendered generically, just mapped to friendly display units
 *    (minutes/seconds instead of raw ms) for four specific real keys.
 * Every other real setting (Tracking/TripCues/Safety/Sync/Admin/Backend
 * groups), the theme picker, and admin-PIN rotation still work exactly as
 * before — tucked under "Advanced" rather than removed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import { useRouteConfig } from "../settings/useRouteConfig";
import type { RouteConfig } from "../settings/routeConfig";
import type { Agency } from "../api/types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const AUTH_STORAGE_KEY = "occ.session.v1";

const TIMEZONES = [
  "Asia/Karachi", "Asia/Dubai", "Asia/Kolkata", "Asia/Dhaka",
  "Europe/London", "UTC",
];
const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "ur", label: "Urdu" },
];

interface SchemaEntry {
  key: string;
  type: "int" | "float" | "bool" | "string" | "json";
  default: unknown;
  group: string;
  label: string;
  description?: string;
  min?: number;
  max?: number;
  secret?: boolean;
}

function getToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accessToken?: string };
    return parsed.accessToken ?? null;
  } catch { return null; }
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body = "";
    try { body = await res.text(); } catch { /* ignore */ }
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Local (per-operator) dark/light theme picker — independent of agency settings. */
function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  return (
    <section className="card">
      <h3 style={{ marginTop: 0 }}>Appearance</h3>
      <div className="form-row" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <label htmlFor="occ-theme">Theme</label>
        <select id="occ-theme" value={theme} onChange={(e) => setTheme(e.target.value === "light" ? "light" : "dark")}>
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
        <span className="secondary-text">Saved on this device. Map tiles follow the theme.</span>
      </div>
    </section>
  );
}

/**
 * Measured per-route distances feeding the Run Kilometres payable
 * calculation. Revenue length is per trip; the two dead-mileage figures are
 * incurred once per bus per day. Stored per browser (see
 * settings/routeConfig.ts) until the agency settings schema carries
 * `occ.route_km.*` keys.
 */
type KmField = keyof RouteConfig;

const KM_FIELDS: Array<{ key: KmField; label: string; hint: string }> = [
  { key: "lengthKm",  label: "Route length",     hint: "One trip, terminal to terminal" },
  { key: "deadOutKm", label: "Depot → terminal", hint: "Dead mileage, once per bus per day" },
  { key: "deadInKm",  label: "Terminal → depot", hint: "Dead mileage, once per bus per day" },
];

function RouteDistancesCard() {
  const [config, save] = useRouteConfig();
  // Local drafts so a half-typed "1." isn't parsed away mid-keystroke.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newRoute, setNewRoute] = useState("");

  const draftKey = (route: string, field: KmField) => `${route}::${field}`;

  const valueFor = (route: string, field: KmField) =>
    drafts[draftKey(route, field)] ?? String(config[route]?.[field] ?? "");

  const clearDraft = (route: string, field: KmField) =>
    setDrafts((d) => {
      const { [draftKey(route, field)]: _drop, ...rest } = d;
      return rest;
    });

  const commit = (route: string, field: KmField, raw: string) => {
    const n = Number(raw);
    const current = config[route];
    if (!current) return;
    // Route length must be positive; dead mileage may legitimately be zero
    // (a terminal that doubles as the depot).
    const valid =
      raw.trim() !== "" && Number.isFinite(n) && (field === "lengthKm" ? n > 0 : n >= 0);
    if (!valid) { clearDraft(route, field); return; }
    save({ ...config, [route]: { ...current, [field]: n } });
    clearDraft(route, field);
  };

  const remove = (route: string) => {
    const { [route]: _drop, ...rest } = config;
    save(rest);
  };

  const addRoute = () => {
    const name = newRoute.trim();
    if (!name || config[name]) return;
    save({ ...config, [name]: { lengthKm: 0, deadOutKm: 0, deadInKm: 0 } });
    setNewRoute("");
  };

  const routes = Object.keys(config).sort();

  return (
    <section className="card">
      <h3 style={{ marginTop: 0 }}>Measured route distances</h3>
      <p className="secondary-text" style={{ marginTop: 0 }}>
        All values in kilometres. Run Kilometres prices each counted trip at
        the route length and adds dead mileage once per bus per day to reach
        payable kilometres. Saved on this device.
      </p>

      <table className="data-table compact" style={{ marginTop: 0 }}>
        <thead>
          <tr>
            <th>Route</th>
            {KM_FIELDS.map((f) => (
              <th key={f.key} style={{ width: 150 }} title={f.hint}>{f.label} (km)</th>
            ))}
            <th className="num">Total / bus / day</th>
            <th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {routes.length === 0 && (
            <tr><td colSpan={6} className="empty">No routes configured yet.</td></tr>
          )}
          {routes.map((route) => {
            const cfg = config[route];
            return (
              <tr key={route}>
                <td>{route}</td>
                {KM_FIELDS.map((f) => (
                  <td key={f.key}>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      title={f.hint}
                      value={valueFor(route, f.key)}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [draftKey(route, f.key)]: e.target.value }))
                      }
                      onBlur={(e) => commit(route, f.key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    />
                  </td>
                ))}
                <td className="num mono secondary-text">
                  {(cfg.deadOutKm + cfg.deadInKm).toFixed(1)} dead
                </td>
                <td>
                  <button className="btn ghost danger" type="button" onClick={() => remove(route)}>
                    Remove
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="settings-form-row" style={{ marginTop: 12, alignItems: "flex-end" }}>
        <label className="settings-field">
          <span>Add route</span>
          <input
            value={newRoute}
            placeholder="e.g. Faiz Ahmad Faiz to Airport"
            onChange={(e) => setNewRoute(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRoute(); }}
          />
        </label>
        <button className="btn primary" type="button" onClick={addRoute}>Add</button>
      </div>
    </section>
  );
}

export default function SettingsPage() {
  const { session } = useAuth();

  // Agency identity.
  const [agency, setAgency] = useState<Agency | null>(null);
  const [agencyName, setAgencyName] = useState("");
  const [agencyTz, setAgencyTz] = useState("Asia/Karachi");
  const [agencyLang, setAgencyLang] = useState("en");
  const [savingAgency, setSavingAgency] = useState(false);

  // Full schema-driven settings (thresholds card reads/writes a curated
  // subset of this same real data).
  const [schema, setSchema] = useState<SchemaEntry[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [thresholdEdits, setThresholdEdits] = useState<Record<string, unknown>>({});
  const [advancedEdits, setAdvancedEdits] = useState<Record<string, unknown>>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [pinDraft, setPinDraft] = useState("");
  const [rotatingPin, setRotatingPin] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, v] = await Promise.all([
        fetchJson<SchemaEntry[]>("/settings/schema"),
        fetchJson<Record<string, unknown>>("/settings"),
      ]);
      setSchema(s);
      setValues(v);
      setThresholdEdits({});
      setAdvancedEdits({});
      if (session?.agencyId) {
        const a = await api.getAgency(session.agencyId);
        setAgency(a);
        setAgencyName(a.name ?? "");
        setAgencyTz(a.timezone || "Asia/Karachi");
        setAgencyLang(a.language || "en");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [session?.agencyId]);

  useEffect(() => { load(); }, [load]);

  const live = useCallback(
    (key: string) => (thresholdEdits[key] ?? advancedEdits[key] ?? values[key]) as unknown,
    [thresholdEdits, advancedEdits, values]
  );

  const lateCritSec = Number(live("occ.late_crit_seconds") ?? 300);
  const staleGpsMs = Number(live("tracking.max_location_age_ms") ?? 120000);
  const fleetRefreshMs = Number(live("occ.fleet_fallback_poll_ms") ?? 3000);
  const alertsRefreshMs = Number(live("occ.alerts_fallback_poll_ms") ?? 5000);
  const audibleAlarm = Boolean(live("occ.panic_audible_alarm") ?? true);

  const agencyDirty = agency != null && (
    agencyName !== (agency.name ?? "") ||
    agencyTz !== (agency.timezone || "Asia/Karachi") ||
    agencyLang !== (agency.language || "en")
  );
  const thresholdsDirty = Object.keys(thresholdEdits).length > 0;
  const advancedDirty = Object.keys(advancedEdits).length > 0;

  async function onSaveAgency() {
    if (!agency || !agencyDirty) return;
    setSavingAgency(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.updateAgency(agency.id, {
        name: agencyName.trim(),
        timezone: agencyTz,
        language: agencyLang,
      });
      setAgency(updated);
      setNotice("Agency settings saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingAgency(false);
    }
  }

  async function onSaveThresholds() {
    if (!thresholdsDirty) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await fetchJson<void>("/settings", { method: "PUT", body: JSON.stringify(thresholdEdits) });
      setNotice("Operations thresholds saved.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onSaveAdvanced() {
    if (!advancedDirty) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await fetchJson<void>("/settings", { method: "PUT", body: JSON.stringify(advancedEdits) });
      setNotice("Saved.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const onRotatePin = useCallback(async () => {
    const pin = pinDraft.trim();
    if (pin.length < 4) { setError("PIN must be at least 4 digits"); return; }
    setRotatingPin(true);
    setError(null);
    setNotice(null);
    try {
      await fetchJson<void>("/settings/admin-pin", { method: "POST", body: JSON.stringify({ new_pin: pin }) });
      setPinDraft("");
      setNotice("Admin PIN rotated.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "PIN rotation failed");
    } finally {
      setRotatingPin(false);
    }
  }, [pinDraft, load]);

  const groups = useMemo(() => {
    const out: Record<string, SchemaEntry[]> = {};
    for (const entry of schema) (out[entry.group] ||= []).push(entry);
    return out;
  }, [schema]);

  function onAdvancedChange(entry: SchemaEntry, raw: string) {
    let parsed: unknown = raw;
    if (entry.type === "int") {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) return;
      parsed = n;
    } else if (entry.type === "float") {
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return;
      parsed = n;
    } else if (entry.type === "bool") {
      parsed = raw === "true";
    }
    setAdvancedEdits((prev) => ({ ...prev, [entry.key]: parsed }));
  }

  if (loading) {
    return <div className="page settings-page"><div className="empty">Loading settings…</div></div>;
  }

  return (
    <div className="page settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="secondary-text">Agency configuration and operational thresholds.</p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}
      {notice && <div className="banner">{notice}</div>}

      <div className="settings-hero-grid">
        <section className="card settings-card">
          <h3>Agency</h3>
          <label className="settings-field">
            <span>Agency name</span>
            <input value={agencyName} onChange={(e) => setAgencyName(e.target.value)} />
          </label>
          <div className="settings-form-row">
            <label className="settings-field">
              <span>Timezone</span>
              <select value={agencyTz} onChange={(e) => setAgencyTz(e.target.value)}>
                {!TIMEZONES.includes(agencyTz) && <option value={agencyTz}>{agencyTz}</option>}
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </label>
            <label className="settings-field">
              <span>Language</span>
              <select value={agencyLang} onChange={(e) => setAgencyLang(e.target.value)}>
                {!LANGUAGES.some((l) => l.code === agencyLang) && <option value={agencyLang}>{agencyLang}</option>}
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
          </div>
        </section>

        <section className="card settings-card">
          <h3>Operations thresholds</h3>
          <div className="settings-form-row">
            <label className="settings-field">
              <span>Late threshold</span>
              <div className="settings-suffix-field">
                <input
                  type="number" min={0}
                  value={Math.round(lateCritSec / 60)}
                  onChange={(e) => setThresholdEdits((s) => ({ ...s, "occ.late_crit_seconds": Math.max(0, Number(e.target.value) || 0) * 60 }))}
                />
                <span className="settings-suffix">min</span>
              </div>
            </label>
            <label className="settings-field">
              <span>Stale GPS after</span>
              <div className="settings-suffix-field">
                <input
                  type="number" min={0}
                  value={Math.round(staleGpsMs / 1000)}
                  onChange={(e) => setThresholdEdits((s) => ({ ...s, "tracking.max_location_age_ms": Math.max(0, Number(e.target.value) || 0) * 1000 }))}
                />
                <span className="settings-suffix">s</span>
              </div>
            </label>
          </div>
          <div className="settings-form-row">
            <label className="settings-field">
              <span>Fleet refresh</span>
              <div className="settings-suffix-field">
                <input
                  type="number" min={1}
                  value={Math.round(fleetRefreshMs / 1000)}
                  onChange={(e) => setThresholdEdits((s) => ({ ...s, "occ.fleet_fallback_poll_ms": Math.max(1, Number(e.target.value) || 1) * 1000 }))}
                />
                <span className="settings-suffix">s</span>
              </div>
            </label>
            <label className="settings-field">
              <span>Alerts refresh</span>
              <div className="settings-suffix-field">
                <input
                  type="number" min={1}
                  value={Math.round(alertsRefreshMs / 1000)}
                  onChange={(e) => setThresholdEdits((s) => ({ ...s, "occ.alerts_fallback_poll_ms": Math.max(1, Number(e.target.value) || 1) * 1000 }))}
                />
                <span className="settings-suffix">s</span>
              </div>
            </label>
          </div>

          <button
            type="button"
            className={"settings-toggle-row" + (audibleAlarm ? " on" : "")}
            onClick={() => setThresholdEdits((s) => ({ ...s, "occ.panic_audible_alarm": !audibleAlarm }))}
          >
            <span className="settings-toggle" role="switch" aria-checked={audibleAlarm} />
            Audible alarm on panic
          </button>

          <div className="settings-actions-row">
            <button className="btn primary settings-save-btn" type="button" disabled={!thresholdsDirty || saving} onClick={onSaveThresholds}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </section>
      </div>

      {agencyDirty && (
        <div className="settings-agency-save-bar">
          <span className="secondary-text">Agency card has unsaved changes.</span>
          <button className="btn primary" type="button" disabled={savingAgency} onClick={onSaveAgency}>
            {savingAgency ? "Saving…" : "Save agency"}
          </button>
        </div>
      )}

      <details className="card settings-advanced" style={{ padding: 0 }} open={showAdvanced} onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}>
        <summary>Advanced: appearance, every tunable setting, admin PIN</summary>
        <div className="settings-advanced-body">
          <AppearanceCard />

          <RouteDistancesCard />

          <div className="card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button className="btn" disabled={!advancedDirty || saving} onClick={onSaveAdvanced}>
              {saving ? "Saving…" : `Save (${Object.keys(advancedEdits).length})`}
            </button>
            <button className="btn ghost" disabled={!advancedDirty} onClick={() => setAdvancedEdits({})}>Discard changes</button>
          </div>

          {Object.entries(groups).map(([group, entries]) => (
            <section key={group} className="card">
              <h3 style={{ marginTop: 0 }}>{group}</h3>
              <table className="data-table compact">
                <tbody>
                  {entries.map((entry) => {
                    const v = (advancedEdits[entry.key] ?? values[entry.key] ?? entry.default) as unknown;
                    return (
                      <tr key={entry.key}>
                        <td style={{ width: "40%" }}>
                          <div>{entry.label}</div>
                          <div className="secondary-text">{entry.key}</div>
                        </td>
                        <td>
                          {entry.secret ? (
                            <span className="secondary-text">{v === "<set>" ? "configured" : "not set"}</span>
                          ) : entry.type === "bool" ? (
                            <select value={String(v)} onChange={(e) => onAdvancedChange(entry, e.target.value)}>
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : entry.type === "int" || entry.type === "float" ? (
                            <input
                              type="number" step={entry.type === "float" ? "any" : 1}
                              min={entry.min} max={entry.max}
                              value={String(v)}
                              onChange={(e) => onAdvancedChange(entry, e.target.value)}
                              style={{ width: 140 }}
                            />
                          ) : (
                            <input type="text" value={String(v ?? "")} onChange={(e) => onAdvancedChange(entry, e.target.value)} style={{ width: 200 }} />
                          )}
                          {entry.description ? <div className="secondary-text">{entry.description}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Admin PIN rotation</h3>
            <p className="secondary-text">
              Sets a new driver-console admin PIN for this agency. The bcrypt hash
              replaces <code>admin.pin_hash</code> in agency_settings.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="password" inputMode="numeric" placeholder="New PIN (4-16 digits)"
                value={pinDraft} onChange={(e) => setPinDraft(e.target.value)} style={{ width: 200 }}
              />
              <button className="btn" disabled={rotatingPin || pinDraft.length < 4} onClick={onRotatePin}>
                {rotatingPin ? "Rotating…" : "Rotate PIN"}
              </button>
            </div>
          </section>
        </div>
      </details>
    </div>
  );
}
